import { selectInfusionDosesAdministeredByDrugId } from './drugs-administered'
import { createDoseProp } from '../utils/dose-prop'
import {
  removeKeysWithNullishValues,
  replaceObjectRawKeys,
} from '../utils/object'
import { createSelector } from '../utils/selector-utils'
import { selectPatientWeightInKg } from './height-weight'
import { toNumber } from '../utils/numbers'

const prop = createDoseProp({
  route: 'infusion',
  drugInfoProp: 'infusionDrugs',
  mapFn: replaceObjectRawKeys,
})

/**
 * @param {InfusionDrug} infusionDrug The drug we're selecting
 * @returns {any} The changes to apply to the case.
 */
export const actionSelectInfusionDrug = ({ id, name }) =>
  prop.actionSelect(id, { name })

/**
 * @param {string} id The rxcui ID of the drug we're unselecting
 * @returns {any} The changes to apply to the case.
 */
export const actionUnselectInfusionDrug = prop.actionUnselect

/**
 * @param {string} id
 * @param {{
 *   amount_raw?: string
 *   wasted_raw?: string
 *   used_raw?: string
 *   route?: InfusionRoute
 *   unit?: InfusionUnit
 *   bolusUnit?: InfusionBolusUnit
 *   wasteConfirmed?: boolean
 *   totalUnit?: InfusionTotalUnit
 *   kg?: string
 *   meta?: 'totals' | null
 * }} updates
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateInfusionDosage = (id, updates) =>
  prop.actionUpdateDosage(id, updates)

/**
 * We do this as an action creator, because its important that we capture
 * totalUnit as part of confirming totals for infusions. Up unit totals are
 * confirmed we derive totalUnit from complex variables within the app. This
 * writes it to the case object so we can use it for running reports and analytics.
 *
 * @param {string} id
 * @param {InfusionTotalUnit} totalUnit
 * @returns {any} The changes to apply to the case.
 */
export const actionConfirmInfusionTotals = (id, totalUnit) =>
  actionUpdateInfusionDosage(id, { totalUnit, wasteConfirmed: true })

/**
 * @param {string} id
 * @param {InfusionUnit} unit
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateInfusionDrugUnit = (id, unit) =>
  prop.actionUpdateDosage(id, { unit })

/**
 * @param {string} id
 * @param {InfusionBolusUnit} bolusUnit
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateInfusionDrugBolusUnit = (id, bolusUnit) =>
  prop.actionUpdateDosage(id, { bolusUnit })

/**
 * @param {string} id
 * @param {string} amountRaw
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateInfusionDrugAmount = (id, amountRaw) =>
  prop.actionUpdateDosage(id, { amount_raw: amountRaw })

/**
 * @param {MacroInfusionDrug} macroInfusionDrug
 * @returns {any} The changes to apply to the case.
 */
export const actionPresetInfusionDrug = ({
  id,
  name,
  dose,
  unit,
  bolusUnit,
}) => ({
  ...actionSelectInfusionDrug({ id, name }),
  ...actionUpdateInfusionDrugAmount(id, dose),
  ...actionUpdateInfusionDrugUnit(id, unit),
  ...actionUpdateInfusionDrugBolusUnit(id, bolusUnit),
})

/** @type {(caseData: CaseData) => CaseData['infusionDrugs']} */
export const selectCaseInfusionDrugs = prop.selectDrugInfoRaw

/**
 * @type {(
 *   caseData: CaseData
 * ) => { [drugId: string]: InfusionDrugDosageProcessed }}
 */
export const selectInfusionDrugDosage = caseData => prop.selectDosage(caseData)

/** @type {InfusionBolusUnit[]} */
const baseInfusionBolusUnits = ['mcg', 'mg']
/** @type {InfusionBolusUnit[]} */
const infusionBolusUnits = ['mcg/kg', ...baseInfusionBolusUnits]
/** @type {(caseData: CaseData) => SelectedInfusionDrug[]} */
export const selectSelectedInfusionDrugs = createSelector(
  prop.selectSelectedDrugs,
  selectPatientWeightInKg,
  /**
   * @param {InfusionDrugDosage[]} drugs
   * @param {number | null} patientWeightInKg
   * @returns {SelectedInfusionDrug[]}
   */
  (drugs, patientWeightInKg) =>
    drugs.map(drug => {
      /**
       * Setting the defaults here for the unit and bolus unit so they don't
       * have to be manually set when selecting a drug. We also set the pumpKg
       * to the patient weight in kg if it's not set.
       */
      const unit = drug.unit || 'mcg/kg/min'
      let bolusUnit = drug.bolusUnit || 'mcg/kg'
      const unitNeedsKg = unit.includes('kg')

      /**
       * We purposely limit the options for boluses here based on the unit of
       * the infusion. This is because we will derive possible and therefore
       * default units for boluses based on the unit of the infusion.
       */
      const bolusUnitOptions = unitNeedsKg
        ? infusionBolusUnits
        : baseInfusionBolusUnits

      /**
       * If you set the infusion unit to something without kg it doesn't make
       * sense to have the bolus unit set to something that relates to kg. The
       * pump won't let you bolus based on weight in that scenario. So we
       * default guess the best option if it's set to one that is invalid.
       */
      if (!bolusUnitOptions.includes(bolusUnit)) {
        bolusUnit = unit.includes('mcg') ? 'mcg' : 'mg'
      }

      return {
        ...drug,
        pumpKg: toNumber(drug.kg || patientWeightInKg) || null,
        unitNeedsKg,
        unit,
        bolusUnit,
        bolusUnitOptions,
      }
    })
)

/**
 * @type {(
 *   caseData: CaseData
 * ) => { [drugId: string]: SelectedInfusionDrug }}
 */
export const selectSelectedInfusionDrugsById = prop.selectSelectedDrugsById

/** @type {CaseSelector<MacroInfusionDrug[]>} */
export const selectInfusionDrugsForMacro = createSelector(
  selectSelectedInfusionDrugs,
  selectInfusionDosesAdministeredByDrugId,
  (selectedDrugs, administeredByDrugId) =>
    selectedDrugs.map(({ id, name, amount_raw, unit, bolusUnit }) => {
      const administered = administeredByDrugId[id] || []
      const firstDose = administered[0]
      return removeKeysWithNullishValues({
        id,
        name,
        unit,
        bolusUnit,
        dose: (firstDose && firstDose.amount_raw) || amount_raw,
      })
    })
)
